/**
 * Input validation utilities
 */

export const validateCampaignTitle = (title) => {
  const errors = [];
  
  if (!title || title.trim().length === 0) {
    errors.push('Title is required');
  } else if (title.trim().length < 3) {
    errors.push('Title must be at least 3 characters long');
  } else if (title.trim().length > 100) {
    errors.push('Title must be less than 100 characters');
  }
  
  // Check for potentially harmful content
  const harmfulPatterns = [/<script/i, /javascript:/i, /on\w+=/i];
  if (harmfulPatterns.some(pattern => pattern.test(title))) {
    errors.push('Title contains invalid characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: title ? title.trim() : ''
  };
};

export const validateCampaignDescription = (description) => {
  const errors = [];
  
  if (!description || description.trim().length === 0) {
    errors.push('Description is required');
  } else if (description.trim().length < 10) {
    errors.push('Description must be at least 10 characters long');
  } else if (description.trim().length > 2000) {
    errors.push('Description must be less than 2000 characters');
  }
  
  // Check for potentially harmful content
  const harmfulPatterns = [/<script/i, /javascript:/i, /on\w+=/i];
  if (harmfulPatterns.some(pattern => pattern.test(description))) {
    errors.push('Description contains invalid characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: description ? description.trim() : ''
  };
};

export const validateGoalAmount = (amount) => {
  const errors = [];
  const numAmount = parseFloat(amount);
  
  if (!amount || amount.toString().trim().length === 0) {
    errors.push('Goal amount is required');
  } else if (isNaN(numAmount)) {
    errors.push('Goal amount must be a valid number');
  } else if (numAmount <= 0) {
    errors.push('Goal amount must be greater than 0');
  } else if (numAmount < 100) {
    errors.push('Goal amount must be at least ₹100');
  } else if (numAmount > 10000000) {
    errors.push('Goal amount cannot exceed ₹10,000,000');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: numAmount
  };
};

export const validateDuration = (duration) => {
  const errors = [];
  const numDuration = parseInt(duration);
  
  if (!duration || duration.toString().trim().length === 0) {
    errors.push('Duration is required');
  } else if (isNaN(numDuration)) {
    errors.push('Duration must be a valid number');
  } else if (numDuration < 1) {
    errors.push('Duration must be at least 1 day');
  } else if (numDuration > 365) {
    errors.push('Duration cannot exceed 365 days');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: numDuration
  };
};

export const validateDonationAmount = (amount) => {
  const errors = [];
  const numAmount = parseFloat(amount);
  
  if (!amount || amount.toString().trim().length === 0) {
    errors.push('Donation amount is required');
  } else if (isNaN(numAmount)) {
    errors.push('Donation amount must be a valid number');
  } else if (numAmount <= 0) {
    errors.push('Donation amount must be greater than 0');
  } else if (numAmount < 10) {
    errors.push('Minimum donation amount is ₹10');
  } else if (numAmount > 1000000) {
    errors.push('Maximum donation amount is ₹1,000,000');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: numAmount
  };
};

export const validateImageURL = (url) => {
  const errors = [];
  
  if (url && url.trim().length > 0) {
    try {
      new URL(url);
      
      // Check if it's a valid image URL
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const hasValidExtension = validExtensions.some(ext => 
        url.toLowerCase().includes(ext)
      );
      
      const isIPFS = url.includes('ipfs') || url.includes('pinata');
      
      if (!hasValidExtension && !isIPFS) {
        errors.push('URL must be a valid image (jpg, png, gif, webp) or IPFS link');
      }
      
      if (url.length > 500) {
        errors.push('Image URL is too long');
      }
      
    } catch {
      errors.push('Invalid URL format');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized: url ? url.trim() : ''
  };
};

export const sanitizeHTML = (input) => {
  if (!input) return '';
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

export const validateCampaignForm = (formData) => {
  const titleValidation = validateCampaignTitle(formData.title);
  const descriptionValidation = validateCampaignDescription(formData.description);
  const goalValidation = validateGoalAmount(formData.goalAmount);
  const durationValidation = validateDuration(formData.duration);
  const imageValidation = validateImageURL(formData.imageURI);
  
  const allErrors = [
    ...titleValidation.errors,
    ...descriptionValidation.errors,
    ...goalValidation.errors,
    ...durationValidation.errors,
    ...imageValidation.errors
  ];
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    sanitizedData: {
      title: titleValidation.sanitized,
      description: descriptionValidation.sanitized,
      goalAmount: goalValidation.sanitized,
      duration: durationValidation.sanitized,
      imageURI: imageValidation.sanitized,
      category: formData.category,
      creatorInfo: formData.creatorInfo ? sanitizeHTML(formData.creatorInfo.trim()) : ''
    }
  };
};